import { useSearchParams } from 'react-router-dom';

import { BrandMark } from '@tala/design-tokens/brand/BrandMark';

import { GOOGLE_START_URL } from '@/lib/adminApi';

/**
 * `?error=` is set by api-py's google/finish route redirecting back here on
 * a rejected or unusable sign-in. Known codes get a message that tells the
 * admin what to do next; anything else falls back to the raw code rather
 * than hiding a reason this page has not been taught yet.
 */
const KNOWN_LOGIN_ERRORS: Record<string, string> = {
  forbidden: 'Your Google account is not on the admin allow-list.',
  // The one-time code api-py's google/finish exchanges for a session expires
  // quickly (it is single-use, minted per redirect) — this is the ordinary
  // "took too long / opened a stale tab" outcome, not a rejection, so the fix
  // is just to try again rather than to contact anyone.
  expired: 'That sign-in link expired — try Continue with Google again.',
};

function loginErrorMessage(code: string): string {
  return KNOWN_LOGIN_ERRORS[code] ?? `Sign-in failed: ${code}`;
}

export function LoginPage() {
  const [searchParams] = useSearchParams();
  const error = searchParams.get('error');

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm rounded-card border border-border bg-surface-field p-8 text-center">
        <BrandMark size="lg" className="mx-auto text-fg-primary" title="FastFrame" />
        <h1 className="mt-4 text-h3 font-semibold text-fg-primary">FastFrame Admin</h1>
        <p className="mt-2 text-body text-fg-secondary">Sign in with your workspace Google account.</p>
        {error && (
          <p
            role="alert"
            className="mt-4 rounded-card border border-destructive-border bg-destructive-soft px-3 py-2 text-meta text-destructive-soft-foreground"
          >
            {loginErrorMessage(error)}
          </p>
        )}
        <a
          href={GOOGLE_START_URL}
          className="mt-6 inline-flex w-full items-center justify-center rounded-card bg-primary px-4 py-2 text-body font-medium text-primary-foreground hover:bg-primary-hover"
        >
          Continue with Google
        </a>
      </div>
    </div>
  );
}
